import { StyleSheet } from 'aphrodite';
import { SPACE, THEME } from '../../../components-v2/Theme/theme';
import { background, brandPrimaryText, header, mention, titles, white } from '../../../styles/colors';
import { CssSelectors } from '../../../styles/styles';

// LinkedIn parts its pictures with a hairline, not a gutter from our grid.
const LINKEDIN_IMAGE_HAIRLINE = 2;

export const styleSheet = StyleSheet.create({
	circle: {
		padding: 0,
	},
	clickNav: {
		':hover': {
			cursor: 'pointer',
		},
	},
	closeButton: {
		marginLeft: 5,
	},
	iconRow: {
		alignItems: 'center',
		display: 'flex',
		height: 38,
		justifyContent: 'center',
		margin: '0 auto',
		padding: 4,
		...CssSelectors.allChildren(
			{
				height: 24,
				opacity: 0.5,
			},
			'img'
		),
	},
	iconRowSelected: {
		...CssSelectors.allChildren(
			{
				height: 38,
				opacity: 1,
			},
			'img'
		),
	},
	imageDisplayAreaPreview: {
		background,
		boxSizing: 'border-box',
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'flex-start',
		overflow: 'hidden',
		padding: 0,
		position: 'relative',
		...CssSelectors.allDescendants(
			{
				alignItems: 'center',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				paddingBottom: 2,
			},
			'button'
		),
		...CssSelectors.allDescendants(
			{
				objectFit: 'cover',
			},
			'img'
		),
	},
	imageDisplayAreaPreviewColWrap: {
		flexDirection: 'column',
		height: 372,
	},
	imageDisplayAreaPreviewInstagram: {
		justifyContent: 'center',
		padding: 0,
		...CssSelectors.allDescendants(
			{
				maxHeight: 'auto',
				padding: 0,
			},
			'img'
		),
	},
	imageDisplayAreaPreviewMultiple: {
		paddingBottom: 24,
	},
	imageDisplayAreaPreviewNotMultiple: {
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 8,
	},
	imageDisplayLeft: {
		paddingLeft: 2,
		position: 'relative',
	},
	linkedInImageGrid: {
		background,
		borderBottomLeftRadius: THEME.common.borderRadius,
		borderBottomRightRadius: THEME.common.borderRadius,
		boxSizing: 'border-box',
		display: 'flex',
		gap: LINKEDIN_IMAGE_HAIRLINE,
		overflow: 'hidden',
		...CssSelectors.allDescendants(
			{
				display: 'block',
				height: '100%',
				objectFit: 'cover',
				width: '100%',
			},
			'img'
		),
	},
	linkedInImageLead: {
		flexShrink: 0,
	},
	linkedInImageStack: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: LINKEDIN_IMAGE_HAIRLINE,
		minWidth: 0,
	},
	linkedInImageTile: {
		flex: 1,
		minHeight: 0,
		position: 'relative',
	},
	linkedInHiddenCount: {
		alignItems: 'center',
		background: `color-mix(in srgb, ${THEME.colors.chart.topHatBlack} 55%, transparent)`,
		color: white,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		inset: 0,
		justifyContent: 'center',
		position: 'absolute',
	},

	pointLeft: {
		height: 15,
		transform: 'rotateZ(90deg)',
		width: 20,
	},
	pointRight: {
		height: 15,
		transform: 'rotateZ(270deg)',
		width: 20,
	},
	mentionHighlight: {
		background: mention,
		borderRadius: 4,
		color: THEME.colors.primary.main,
		fontWeight: THEME.typography.fontWeights.semibold,
		paddingBlock: 1,
		paddingInline: 3,
	},
	previewContentBox: {
		background: 'white',
		borderRadius: 8,
		color: 'black',
		fontSize: 14,
		marginBottom: SPACE[8],
		marginTop: SPACE[8],
		opacity: 1,
		overflowWrap: 'break-word',
		transform: 'translate3d(0,0,0)',
		transition: 'opacity 0.2s, transform 0.5s',
		whiteSpace: 'pre-wrap',
		width: 372,
		wordBreak: 'break-word',
		wordWrap: 'break-word',
	},
	previewContentBoxInstagram: {},
	previewContentContainer: {
		color: brandPrimaryText,
		fontSize: 16,
		overflow: 'auto',
	},
	previewContentSection: {
		alignItems: 'center',
		background: mention,
		display: 'flex',
		flexDirection: 'column',
		position: 'relative',
		paddingBottom: SPACE[16],
		paddingTop: SPACE[16],
	},
	previewDisclaimerTooltip: {
		color: titles,
		fontSize: THEME.typography.fontSizes.xs,
		lineHeight: '18px',
		maxWidth: 300,
		padding: SPACE[8],
	},
	previewDisclaimerTrigger: {
		alignItems: 'center',
		background: 'none',
		border: 'none',
		borderRadius: '50%',
		cursor: 'pointer',
		display: 'flex',
		padding: 0,
		':focus-visible': {
			outline: `2px solid ${THEME.colors.primary.main}`,
			outlineOffset: 2,
		},
	},
	previewHeader: {
		margin: '0 auto',
	},
	previewHeaderRow: {
		alignItems: 'center',
		display: 'flex',
		gap: SPACE[4],
		justifyContent: 'center',
	},
	radioBackground: {
		background,
	},
	tab: {
		alignItems: 'center',
		boxSizing: 'border-box',
		color: header,
		cursor: 'pointer',
		display: 'flex',
		flexShrink: 0,
		fontSize: 12,
		justifyContent: 'center',
		letterSpacing: 1.05,
		textTransform: 'uppercase',
		whiteSpace: 'nowrap',
	},
	tabNavigation: {
		...CssSelectors.allChildren(
			{
				paddingLeft: 6,
			},
			':not(:first-child), label'
		),
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'center',
		paddingBottom: 6,
		paddingTop: 6,
	},
	textOnlyPadding: {
		padding: 14,
	},
	// Both layers share one cell, so the caption keeps the height of the platform that shows the most
	// and switching platforms cannot resize the card under the options and buttons below it.
	captionArea: {
		display: 'grid',
	},
	// The same trick for the whole card: every platform's version stacks in one cell, which holds the
	// tallest of them. The shown card sits at the top, and the space it leaves is the section's own.
	previewCardArea: {
		alignItems: 'flex-start',
		display: 'grid',
	},
	previewCardLayer: {
		gridArea: '1 / 1',
	},
	previewCardSizer: {
		gridArea: '1 / 1',
		visibility: 'hidden',
	},
	captionLayer: {
		gridArea: '1 / 1',
		minWidth: 0,
	},
	captionSizer: {
		gridArea: '1 / 1',
		minWidth: 0,
		visibility: 'hidden',
	},
	signatureBadge: {
		color: THEME.colors.success.dark,
		position: 'absolute',
		right: SPACE[16],
		top: SPACE[16],
	},
	disclosureContent: {
		padding: '0 14px 14px 14px',
		fontSize: 'inherit',
		color: 'inherit',
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
		wordWrap: 'break-word',
		marginTop: 4,
	},
	videoWrapper: {
		position: 'relative',
	},
});
